import { Link, useLocation } from 'react-router-dom'
import { ChevronRight, Home } from 'lucide-react'
import { useUser } from '@/contexts/UserContext'
import { useBreadcrumbContext } from '@/contexts/BreadcrumbContext'
import { findDestination } from '@/config/navigationConfig'
import { cn } from '@/lib/utils'

interface Crumb {
  label: string
  href?: string
}

/**
 * Salesforce-style breadcrumb trail: Home > Object > Record.
 * - "Home" and the object (e.g. "Accounts") are links.
 * - The object crumb is derived from the URL via findDestination().
 * - The record name (current page) is supplied by detail pages via
 *   useRecordBreadcrumb(); list pages have no record crumb, so the object is the
 *   current page.
 * Responsive: on small screens only the last two crumbs are shown.
 */
export function Breadcrumbs() {
  const { pathname } = useLocation()
  const { effectiveUserType } = useUser()
  const { recordLabel } = useBreadcrumbContext()

  // No breadcrumb on the dashboard — it would be a lone "Home".
  if (pathname === '/') return null

  const crumbs: Crumb[] = [{ label: 'Home', href: '/' }]

  const dest = findDestination(pathname, effectiveUserType)
  if (dest) {
    // When a record name is present (detail page) the object becomes a link and
    // the record is the current page; otherwise the object itself is current.
    crumbs.push({ label: dest.label, href: recordLabel ? dest.href : undefined })
  }
  if (recordLabel) {
    crumbs.push({ label: recordLabel })
  }

  // Unmapped route with no record → nothing useful to show.
  if (crumbs.length === 1) return null

  const lastIndex = crumbs.length - 1

  return (
    <nav aria-label="Breadcrumb" className="mb-4">
      <ol className="flex items-center gap-1.5 text-sm">
        {crumbs.map((crumb, i) => {
          const isLast = i === lastIndex
          const isHome = i === 0
          // Show only the last two crumbs on small screens.
          const hideOnMobile = i < lastIndex - 1
          return (
            <li
              key={`${crumb.label}-${i}`}
              className={cn('flex min-w-0 items-center gap-1.5', hideOnMobile && 'hidden sm:flex')}
            >
              {i > 0 && (
                <ChevronRight
                  className="h-4 w-4 flex-shrink-0 text-muted-foreground"
                  aria-hidden="true"
                />
              )}
              {crumb.href && !isLast ? (
                <Link
                  to={crumb.href}
                  className="flex max-w-[12rem] items-center gap-1 truncate rounded-sm text-muted-foreground hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  title={crumb.label}
                >
                  {isHome ? (
                    <>
                      <Home className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                      <span className="sr-only sm:not-sr-only">{crumb.label}</span>
                    </>
                  ) : (
                    <span className="truncate">{crumb.label}</span>
                  )}
                </Link>
              ) : (
                <span
                  className="max-w-[16rem] truncate font-medium text-foreground"
                  aria-current={isLast ? 'page' : undefined}
                  title={crumb.label}
                >
                  {crumb.label}
                </span>
              )}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
